import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { motion } from 'motion/react';
import { useConfirm } from '../common/ConfirmDialog';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Paper,
  Avatar,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Apps as AppsIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useApi } from '../../hooks/useApi';
import { getCustomerIdFromSubdomain } from '../../utils/constants';
import { filterInput } from '../../utils/inputSanitization';
import CustomApplicationDetailView from './CustomApplicationDetailView';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';
import { ReadOnlyButton } from '../common/ReadOnlyButton';

// Minimalist input style
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: '#27AE60' },
    '&.Mui-focused fieldset': { borderColor: '#27AE60' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: '#27AE60' },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
  },
};

const CustomApplicationConfig = forwardRef((props, ref) => {
  const confirm = useConfirm();
  const isReadOnly = useReadOnlyMode();
  const {
    customApplications,
    createCustomApplication,
    fetchCustomApplications,
    updateCustomApplication,
    deleteCustomApplication,
    uploadCustomApplicationIcon,
    deleteCustomApplicationIcon,
    loading,
  } = useApi();

  // List/detail toggle state
  const [selectedApp, setSelectedApp] = useState(null);

  // Create/Edit dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    clientId: '',
    description: '',
    enabled: true,
    assertionConsumerUrl: '',
    singleLogoutUrl: '',
    nameIdFormat: 'persistent',
    signAssertions: true,
    signResponse: true,
    clientSignature: false,
    relayState: '',
  });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  const customerId = getCustomerIdFromSubdomain();

  useImperativeHandle(ref, () => ({
    openAddDialog: () => setShowDialog(true),
    isDetailView: () => selectedApp !== null,
  }));

  useEffect(() => {
    if (customerId) {
      fetchCustomApplications(customerId);
    }
  }, [customerId]);

  // Notify parent when selectedApp changes (for hiding page header elements)
  useEffect(() => {
    if (props.onDetailViewChange) {
      props.onDetailViewChange(selectedApp !== null);
    }
  }, [selectedApp]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.clientId || !formData.assertionConsumerUrl) {
      toast.error('Name, Client ID, and Assertion Consumer URL are required');
      return;
    }
    try {
      let appId;
      if (editingApp) {
        await updateCustomApplication(customerId, editingApp.id, formData);
        appId = editingApp.id;
      } else {
        const result = await createCustomApplication(customerId, formData);
        appId = result?.id;
      }
      // Upload icon if a file was selected
      if (iconFile && appId) {
        try {
          await uploadCustomApplicationIcon(customerId, appId, iconFile);
        } catch (iconError) {
          console.error('Failed to upload icon:', iconError);
          toast.warning('Application saved but icon upload failed');
        }
      }
      handleCloseDialog();
      // Refresh to pick up icon changes
      if (customerId) {
        await fetchCustomApplications(customerId);
      }
      // If we were editing the currently selected app, update it
      if (editingApp && selectedApp && editingApp.id === selectedApp.id) {
        // Refresh the selected app data from the updated list
        const refreshed = customApplications.find(a => a.id === editingApp.id);
        if (refreshed) {
          setSelectedApp({ ...refreshed, ...formData });
        }
      }
    } catch (error) {
      console.error('Failed to save custom application:', error);
    }
  };

  const handleEdit = async (app) => {
    setEditingApp(app);
    setFormData({
      name: app.name,
      clientId: app.clientId,
      description: app.description || '',
      enabled: app.enabled,
      assertionConsumerUrl: app.assertionConsumerUrl,
      singleLogoutUrl: app.singleLogoutUrl || '',
      nameIdFormat: app.nameIdFormat || 'persistent',
      signAssertions: app.signAssertions,
      signResponse: app.signResponse,
      clientSignature: app.clientSignature,
      relayState: app.relayState || '',
    });
    setIconFile(null);
    setIconPreview(app.hasIcon ? `/api/v1/customers/${customerId}/custom-applications/${app.id}/icon` : null);
    setShowDialog(true);
  };

  const handleDelete = async (app) => {
    const confirmed = await confirm({
      title: 'Delete Application',
      message: `Are you sure you want to delete "${app.name}"?`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      await deleteCustomApplication(customerId, app.id);
      // Return to list view if deleting the currently viewed app
      if (selectedApp && selectedApp.id === app.id) {
        setSelectedApp(null);
      }
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingApp(null);
    setFormData({
      name: '', clientId: '', description: '', enabled: true,
      assertionConsumerUrl: '', singleLogoutUrl: '', nameIdFormat: 'persistent',
      signAssertions: true, signResponse: true, clientSignature: false, relayState: '',
    });
    setIconFile(null);
    setIconPreview(null);
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error('Icon file must be less than 512KB');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
      toast.error('Only PNG, JPEG, SVG, and WebP images are allowed');
      return;
    }
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const handleRemoveIcon = async () => {
    if (editingApp && editingApp.hasIcon && !iconFile) {
      try {
        await deleteCustomApplicationIcon(customerId, editingApp.id);
        if (customerId) fetchCustomApplications(customerId);
      } catch (error) {
        console.error('Failed to delete icon:', error);
      }
    }
    setIconFile(null);
    setIconPreview(null);
  };

  if (loading && customApplications.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress sx={{ color: '#27AE60' }} />
      </Box>
    );
  }

  // Detail view
  if (selectedApp) {
    // Find the latest version of the app from the list (in case it was updated)
    const currentApp = customApplications.find(a => a.id === selectedApp.id) || selectedApp;
    return (
      <>
        <CustomApplicationDetailView
          app={currentApp}
          onBack={() => setSelectedApp(null)}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Create/Edit Application Dialog (shared) */}
        {renderDialog()}
      </>
    );
  }

  // List view
  return (
    <Paper elevation={0} sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E5E5' }}>
      <Box sx={{ p: 3 }}>
        {customApplications.length === 0 ? (
          <Alert severity="info" sx={{ backgroundColor: '#EBF5FB', border: '1px solid #4698D320', '& .MuiAlert-icon': { color: '#4698D3' } }}>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#253E66' }}>
              No custom SAML applications configured. Click "Add Application" to create one.
            </Typography>
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {customApplications.map((app, index) => (
              <Box
                key={app.id}
                onClick={() => setSelectedApp(app)}
                sx={{
                  '@keyframes fadeInItem': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: '#FFFFFF',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: '#27AE60',
                    backgroundColor: '#FCFCFC',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                  <Avatar
                    src={app.hasIcon ? `/api/v1/customers/${customerId}/custom-applications/${app.id}/icon` : undefined}
                    sx={{ backgroundColor: '#27AE60', width: 36, height: 36, fontSize: '0.9rem', flexShrink: 0 }}
                  >
                    {!app.hasIcon && <AppsIcon sx={{ fontSize: 20 }} />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#253E66' }}>
                        {app.name}
                      </Typography>
                      <Chip
                        label={app.enabled ? 'Enabled' : 'Disabled'}
                        size="small"
                        sx={{
                          backgroundColor: app.enabled ? '#F0F9F4' : '#FDF2F2',
                          color: app.enabled ? '#27AE60' : '#E74C3C',
                          border: `1px solid ${app.enabled ? '#27AE6020' : '#E74C3C20'}`,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 500,
                          height: 22,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                    {app.description && (
                      <Typography sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.85rem',
                        color: '#999999',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {app.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <ChevronRightIcon sx={{ color: '#CCCCCC', fontSize: 22, flexShrink: 0, ml: 1 }} />
              </Box>
            ))}
          </Box>
        )}

        {/* Create/Edit Application Dialog */}
        {renderDialog()}
      </Box>
    </Paper>
  );

  function renderDialog() {
    return (
      <Dialog open={showDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ backgroundColor: '#F0F9F4', borderBottom: '1px solid #E5E5E5', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, position: 'relative' }}>
          <Avatar sx={{ backgroundColor: '#27AE60', width: 40, height: 40 }}><AppsIcon /></Avatar>
          {editingApp ? 'Edit Application' : 'Create Application'}
          <IconButton onClick={handleCloseDialog} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#666666', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField name="name" label="Application Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: filterInput(e.target.value, 'GENERAL_TEXT') })} required fullWidth size="small" sx={inputStyle} />
              <TextField name="clientId" label="Client ID" value={formData.clientId} onChange={(e) => setFormData({ ...formData, clientId: filterInput(e.target.value, 'URL') })} required disabled={!!editingApp} fullWidth size="small" helperText="Unique identifier or URL (cannot be changed after creation)" sx={inputStyle} />
              <TextField name="description" label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: filterInput(e.target.value, 'GENERAL_TEXT') })} multiline rows={2} fullWidth size="small" sx={inputStyle} />
              <Box>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666', mb: 1 }}>Application Icon (optional)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {iconPreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <Avatar src={iconPreview} variant="rounded" sx={{ width: 48, height: 48, border: '1px solid #E5E5E5' }}>
                        <ImageIcon />
                      </Avatar>
                      <IconButton size="small" onClick={handleRemoveIcon} sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: '#fff', border: '1px solid #E5E5E5', width: 20, height: 20, '&:hover': { backgroundColor: '#fee' } }}>
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Avatar variant="rounded" sx={{ width: 48, height: 48, backgroundColor: '#F0F4F8', border: '1px dashed #E5E5E5' }}>
                      <ImageIcon sx={{ color: '#999' }} />
                    </Avatar>
                  )}
                  <ReadOnlyButton variant="outlined" component={isReadOnly ? undefined : "label"} startIcon={<CloudUploadIcon />} size="small" sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px', '&:hover': { borderColor: '#27AE60', color: '#27AE60' } }}>
                    {iconPreview ? 'Change Icon' : 'Upload Icon'}
                    {!isReadOnly && <input type="file" hidden accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleIconChange} />}
                  </ReadOnlyButton>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999' }}>PNG, JPEG, SVG, WebP. Max 512KB.</Typography>
                </Box>
              </Box>
              <TextField name="assertionConsumerUrl" label="Assertion Consumer Service URL" value={formData.assertionConsumerUrl} onChange={(e) => setFormData({ ...formData, assertionConsumerUrl: filterInput(e.target.value, 'URL') })} required fullWidth size="small" sx={inputStyle} />
              <TextField name="singleLogoutUrl" label="Single Logout URL (Optional)" value={formData.singleLogoutUrl} onChange={(e) => setFormData({ ...formData, singleLogoutUrl: filterInput(e.target.value, 'URL') })} fullWidth size="small" sx={inputStyle} />
              <TextField name="relayState" label="IDP Initiated SSO Relay State (Optional)" value={formData.relayState} onChange={(e) => setFormData({ ...formData, relayState: filterInput(e.target.value, 'URL') })} fullWidth size="small" helperText="URL to redirect users to after SAML authentication" sx={inputStyle} />
              <TextField name="nameIdFormat" label="Name ID Format" value={formData.nameIdFormat} onChange={handleChange} select fullWidth size="small" sx={inputStyle}>
                <MenuItem value="persistent">Persistent</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="unspecified">Unspecified</MenuItem>
              </TextField>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel control={<Switch checked={formData.enabled} onChange={handleChange} name="enabled" sx={{ '& .Mui-checked': { color: '#27AE60' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#27AE60' } }} />} label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>Enabled</Typography>} />
                <FormControlLabel control={<Switch checked={formData.signAssertions} onChange={handleChange} name="signAssertions" sx={{ '& .Mui-checked': { color: '#27AE60' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#27AE60' } }} />} label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>Sign Assertions</Typography>} />
                <FormControlLabel control={<Switch checked={formData.signResponse} onChange={handleChange} name="signResponse" sx={{ '& .Mui-checked': { color: '#27AE60' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#27AE60' } }} />} label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>Sign Response</Typography>} />
                <FormControlLabel control={<Switch checked={formData.clientSignature} onChange={handleChange} name="clientSignature" sx={{ '& .Mui-checked': { color: '#27AE60' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#27AE60' } }} />} label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>Client Signature Required</Typography>} />
              </Box>
            </Box>
            </motion.div>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E5E5', gap: 1.5 }}>
            <Button onClick={handleCloseDialog} variant="outlined" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px' }}>Cancel</Button>
            <ReadOnlyButton type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', borderRadius: '6px', '&:hover': { backgroundColor: '#219A52' } }}>{editingApp ? 'Update' : 'Create'}</ReadOnlyButton>
          </DialogActions>
        </form>
      </Dialog>
    );
  }
});

export default CustomApplicationConfig;
